import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import MenuItem from 'apollo-react/components/MenuItem';
import Modal from 'apollo-react/components/Modal';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { REPORT_DESIGNER } from '../constants/Urls';
import { useTranslation } from '../custom-hooks/useTranslation';
import {
  createNewReport,
  fetchReportModelList,
} from '../report-designer/actions/report-designer.actions';
import { validateReportCreation } from '../report-designer/utils/validation.utils';
import { getUITA } from '../utils/commonUtils';

const useStyles = makeStyles({
  modal: {
    minWidth: '30%',
    '@media (max-width: 1200px)': {
      minWidth: '70%',
    },
    '@media (max-width: 575px)': {
      width: '100%',
    },
    '& [class*="MuiDialogContent"]': {
      paddingBottom: 60,
    },
  },
});

const NewReportDialog = ({ open, handleClose }) => {
  const translateString = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [reportModelList, setReportModelList] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportModelName, setReportModelName] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const fetchReportModels = async () => {
      setLoading(true);
      const reportModels = await dispatch(fetchReportModelList());
      setLoading(false);

      setReportModelList(reportModels);
    };

    fetchReportModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportModelName]);

  const validateForm = async ({ onSave } = {}) => {
    if (onSave || isSubmitted) {
      const valErrors = await validateReportCreation({
        reportName,
        reportModelName,
      });
      setValidationErrors(valErrors);

      return valErrors;
    }
  };

  const handleCreateClick = async () => {
    if (!isSubmitted) {
      setIsSubmitted(true);
    }

    const valErrors = await validateForm({ onSave: true });

    if (!valErrors.length) {
      setLoading(true);
      const newReportId = await dispatch(createNewReport(reportName, reportModelName));

      if (newReportId) {
        history.push(`${REPORT_DESIGNER}?reportId=${newReportId}&standaloneLayout=true`);
      }

      setLoading(false);
    }
  };

  const handleReportModelNameChange = async ({ target: { value } }) => {
    setReportModelName(value);
  };

  const reportNameValErrors = validationErrors.filter(
    ({ errorSource }) => errorSource === 'reportName'
  );
  const reportModelNameValErrors = validationErrors.filter(
    ({ errorSource }) => errorSource === 'reportModelName'
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableBackdropClick
      variant="default"
      title={translateString('Create New Report')}
      buttonProps={[
        {
          label: translateString('Cancel'),
        },
        {
          label: translateString('Create'),
          onClick: handleCreateClick,
          disabled: !!validationErrors.length,
        },
      ]}
      classes={{
        paper: classes.modal,
      }}
    >
      <TextField
        label={translateString('Report Name')}
        fullWidth
        required
        value={reportName}
        onChange={({ target: { value } }) => setReportName(value)}
        onBlur={validateForm}
        error={!!reportNameValErrors.length}
        helperText={
          reportNameValErrors.length ? translateString(reportNameValErrors[0].message) : null
        }
      />
      <Select
        required
        label={translateString('Report Model')}
        placeholder={`${translateString('Select Report Model')}...`}
        fullWidth
        onChange={handleReportModelNameChange}
        value={reportModelName}
        canDeselect={false}
        error={!!reportModelNameValErrors.length}
        helperText={
          reportModelNameValErrors.length
            ? translateString(reportModelNameValErrors[0].message)
            : null
        }
      >
        {reportModelList.map(({ value, label }) => (
          <MenuItem id={getUITA('ReportModelSelect', value)} key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
      {isLoading && <Loader isInner />}
    </Modal>
  );
};

export default NewReportDialog;
